import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog } from "@headlessui/react";
import {
  XMarkIcon,
  CheckIcon,
  MagnifyingGlassIcon,
  ShieldCheckIcon,
} from "@heroicons/react/24/outline";
import { debounce } from "lodash";
import { ResourceLoading } from "../../../utils/ResourceLoading";
import { ResourceError } from "../../../utils/ResourceError";
import { ResourceEmpty } from "../../../utils/ResourceEmpty";
import { fetchChecklistTypes, fetchChecklistType } from "../../../../api/quality/checklistType";
import {
  buildChecklistTypeSelectorFetchParams,
  getChecklistTypeHseBadge,
  getChecklistTypeSelectorItems,
  resolveChecklistCopySource,
  sortChecklistTypesForSelector,
} from "../../quality/checklists/checklistTypeSelectorUtils";
import toast from "react-hot-toast";
import { CustomerSelector } from "../../../utils/moduleSelectors/customerSelector";
import { ProjectSelector } from "../../../utils/moduleSelectors/projectSelector";
import { ProjectFolderSelector } from "../../../utils/moduleSelectors/projectFolderSelector";
import { EmployeeSelector } from "../../../utils/moduleSelectors/employeeSelector";
import { PMDatePicker } from "../../../utils/PMDatePicker";

const TYPE_LIST_FIELDS =
  "id,title,description,items,published_items,source,hse_document,hse_revision_status,active";

const defaultContextDate = () => new Date().toISOString().substring(0, 10);

const emptyContextFields = {
  date: false,
  customer: false,
  project: false,
  folder: false,
  responsible: false,
};

/** Nested module selectors must sit above the checklist selector dialog (z-index 3000). */
const NESTED_SELECTOR_Z_INDEX = 3100;

export const EnhancedChecklistSelector = ({
  open,
  onClose,
  onSelect,
  /** @deprecated Prefer copySourceChecklist */
  mainReportChecklist = null,
  /** @deprecated Prefer copySourceLabel */
  mainReport = null,
  copySourceChecklist = null,
  copySourceLabel = null,
  showTitleField = false,
  defaultTitle = "",
  autoTitleFromType = true,
  showEmptyOption = true,
  showTypeOption = true,
  /** Select a reusable template, without creating a checklist or choosing individual items. */
  typeOnly = false,
  initialType = null,
  /** @type {{ date?: boolean, customer?: boolean, project?: boolean, folder?: boolean, responsible?: boolean }|null} */
  contextFields = null,
  customer = null,
  project = null,
  folder = null,
}) => {
  const { t } = useTranslation();
  const [checklistTypes, setChecklistTypes] = useState([]);
  const [selectedType, setSelectedType] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [typeItems, setTypeItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSearchingTypes, setIsSearchingTypes] = useState(false);
  const [isLoadingItems, setIsLoadingItems] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [title, setTitle] = useState(defaultTitle || "");
  const [titleTouched, setTitleTouched] = useState(false);
  const [mode, setMode] = useState("new");
  const [copyMode, setCopyMode] = useState("all");
  const [contextValues, setContextValues] = useState({
    date: defaultContextDate(),
    customer_id: null,
    project_id: null,
    folder_id: null,
    responsible_executive_id: null,
  });
  const [selectedResponsible, setSelectedResponsible] = useState(null);
  const typesFetchSeqRef = useRef(0);
  const hasLoadedTypesRef = useRef(false);

  const resolvedContextFields = useMemo(
    () => ({ ...emptyContextFields, ...(contextFields || {}) }),
    [contextFields]
  );

  const showContextSection = useMemo(
    () => Object.values(resolvedContextFields).some(Boolean),
    [resolvedContextFields]
  );

  const lockedCustomer = customer?.id ? customer : null;
  const lockedProject = project?.id ? project : null;
  const lockedFolder = folder?.id ? folder : null;

  const contextFormik = useMemo(
    () => ({
      values: contextValues,
      setFieldValue: (name, value) => {
        setContextValues((prev) => {
          const next = { ...prev, [name]: value };
          if (name === "project_id" && value !== prev.project_id) {
            next.folder_id = null;
          }
          return next;
        });
      },
      errors: {},
    }),
    [contextValues]
  );

  const copySource = useMemo(
    () =>
      resolveChecklistCopySource({
        copySourceChecklist,
        mainReportChecklist,
        copySourceLabel,
        mainReport,
      }),
    [copySourceChecklist, mainReportChecklist, copySourceLabel, mainReport]
  );

  const canCopy = copySource.hasItems;

  useEffect(() => {
    if (!open) {
      hasLoadedTypesRef.current = false;
      setSearchQuery("");
      setChecklistTypes([]);
      setError(null);
      setIsLoading(false);
      setIsSearchingTypes(false);
      return;
    }

    setSelectedType(typeOnly ? initialType : null);
    setSelectedItems([]);
    setTypeItems([]);
    setTitle(defaultTitle || "");
    setTitleTouched(false);
    setMode(
      typeOnly ? "type" : showEmptyOption
        ? "new"
        : showTypeOption
          ? "type"
          : canCopy
            ? "main_report"
            : "new"
    );
    setCopyMode("all");
    setContextValues({
      date: defaultContextDate(),
      customer_id: lockedCustomer?.id ?? null,
      project_id: lockedProject?.id ?? null,
      folder_id: lockedFolder?.id ?? null,
      responsible_executive_id: null,
    });
    setSelectedResponsible(null);
  }, [
    open,
    defaultTitle,
    showEmptyOption,
    showTypeOption,
    typeOnly,
    initialType,
    canCopy,
    lockedCustomer?.id,
    lockedProject?.id,
    lockedFolder?.id,
  ]);

  const buildContextPayload = () => {
    if (!showContextSection) return null;

    return {
      date: contextValues.date || defaultContextDate(),
      customer_id: lockedCustomer?.id ?? contextValues.customer_id ?? null,
      project_id: lockedProject?.id ?? contextValues.project_id ?? null,
      folder_id: lockedFolder?.id ?? contextValues.folder_id ?? null,
      responsible_executive_id: contextValues.responsible_executive_id ?? null,
    };
  };

  const validateContextFields = () => {
    if (!showContextSection) return true;

    if (resolvedContextFields.date && !String(contextValues.date || "").trim()) {
      toast.error(t("quality.checklist.selector.context_date_required"));
      return false;
    }

    if (
      resolvedContextFields.customer &&
      !lockedCustomer?.id &&
      !contextValues.customer_id
    ) {
      toast.error(t("quality.checklist.selector.context_customer_required"));
      return false;
    }

    if (
      resolvedContextFields.project &&
      !lockedProject?.id &&
      !contextValues.project_id
    ) {
      toast.error(t("quality.checklist.selector.context_project_required"));
      return false;
    }

    if (
      resolvedContextFields.responsible &&
      !contextValues.responsible_executive_id
    ) {
      toast.error(t("quality.checklist.selector.context_responsible_required"));
      return false;
    }

    return true;
  };

  const loadChecklistTypes = useCallback(
    async (query = "") => {
      const seq = ++typesFetchSeqRef.current;
      const isInitial = !hasLoadedTypesRef.current;

      if (isInitial) {
        setIsLoading(true);
      } else {
        setIsSearchingTypes(true);
      }
      setError(null);

      try {
        const response = await fetchChecklistTypes(
          buildChecklistTypeSelectorFetchParams({
            query,
            fields: TYPE_LIST_FIELDS,
          })
        );
        if (seq !== typesFetchSeqRef.current) return;

        const types = response.data?.data;
        if (!Array.isArray(types)) throw new Error("Invalid checklist type list");
        setChecklistTypes(sortChecklistTypesForSelector(typeOnly ? types.filter((type) => type.active !== false) : types));
        hasLoadedTypesRef.current = true;
      } catch (err) {
        if (seq !== typesFetchSeqRef.current) return;
        setError(t("quality.checklist.selector.load_error"));
      } finally {
        if (seq === typesFetchSeqRef.current) {
          setIsLoading(false);
          setIsSearchingTypes(false);
        }
      }
    },
    [t, typeOnly]
  );

  const debouncedLoadChecklistTypes = useMemo(
    () => debounce((query) => loadChecklistTypes(query), 300),
    [loadChecklistTypes]
  );

  useEffect(() => {
    if (!open) {
      debouncedLoadChecklistTypes.cancel();
      return undefined;
    }

    // Immediate fetch on open / empty query; debounce while typing.
    if (!String(searchQuery || "").trim()) {
      debouncedLoadChecklistTypes.cancel();
      loadChecklistTypes("");
      return undefined;
    }

    debouncedLoadChecklistTypes(searchQuery);
    return () => debouncedLoadChecklistTypes.cancel();
  }, [open, searchQuery, loadChecklistTypes, debouncedLoadChecklistTypes]);

  const loadTypeItems = async (type) => {
    setIsLoadingItems(true);
    try {
      const itemsFromList = getChecklistTypeSelectorItems(type);
      if (itemsFromList.length > 0) {
        setTypeItems(itemsFromList);
        setSelectedItems(itemsFromList.map((item) => item.id));
        return;
      }

      const response = await fetchChecklistType(type.id, {
        fields: TYPE_LIST_FIELDS,
      });
      const checklistType = response.data?.data || response.data;
      const items = getChecklistTypeSelectorItems(checklistType);
      setTypeItems(items);
      setSelectedItems(items.map((item) => item.id));
    } catch (err) {
      toast.error(t("quality.checklist.selector.items_load_error"));
      console.error("Error loading checklist type items:", err);
    } finally {
      setIsLoadingItems(false);
    }
  };

  const resolvedTitle = () => {
    if (showTitleField && titleTouched) {
      return title.trim();
    }
    if (mode === "type" && selectedType && autoTitleFromType) {
      return selectedType.title || "";
    }
    if (defaultTitle) {
      return defaultTitle;
    }
    return title.trim();
  };

  const handleTypeSelect = (type) => {
    setSelectedType(type);
    setMode("type");
    if (!titleTouched && autoTitleFromType && type?.title) {
      setTitle(type.title);
    }
    if (type.id && !typeOnly) {
      const items = getChecklistTypeSelectorItems(type);
      if (items.length > 0) {
        setTypeItems(items);
        setSelectedItems(items.map((item) => item.id));
        setIsLoadingItems(false);
      } else {
        loadTypeItems(type);
      }
    }
  };

  const toggleItemSelection = (itemId) => {
    setSelectedItems((prev) =>
      prev.includes(itemId) ? prev.filter((id) => id !== itemId) : [...prev, itemId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedItems.length === typeItems.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(typeItems.map((item) => item.id));
    }
  };

  const handleCopySelect = () => {
    setMode("main_report");
    setSelectedType(null);
    setTypeItems([]);
    setSelectedItems([]);
  };

  const handleNewChecklist = () => {
    setMode("new");
    setSelectedType(null);
    setTypeItems([]);
    setSelectedItems([]);
    if (!titleTouched) {
      setTitle(defaultTitle || "");
    }
  };

  const handleConfirm = () => {
    if (typeOnly) {
      if (!selectedType?.id || isLoading || isSearchingTypes || error) return;
      onSelect({ mode: "type", checklist_type_id: selectedType.id, checklist_type_title: selectedType.title });
      onClose();
      return;
    }
    if (!validateContextFields()) {
      return;
    }

    const checklistTitle = resolvedTitle();
    const context = buildContextPayload();

    if (mode === "type" && selectedType) {
      if (selectedItems.length === 0) {
        toast.error(t("quality.checklist.selector.select_one_item"));
        return;
      }
      onSelect({
        mode: "type",
        checklist_type_id: selectedType.id,
        checklist_type_title: selectedType.title,
        selected_item_ids: selectedItems,
        title: checklistTitle,
        ...(context ? { context } : {}),
      });
    } else if (mode === "main_report" && copySource.checklist) {
      const items =
        copyMode === "all"
          ? copySource.checklist.items || []
          : (copySource.checklist.items || []).filter((item) => !item.isApproved);

      if (items.length === 0) {
        toast.error(t("quality.checklist.selector.copy_no_items"));
        return;
      }

      onSelect({
        mode: "main_report",
        items,
        include_all: copyMode === "all",
        title: checklistTitle,
        ...(context ? { context } : {}),
      });
    } else if (mode === "new") {
      onSelect({
        mode: "new",
        title: checklistTitle,
        ...(context ? { context } : {}),
      });
    }
    onClose();
  };

  const renderContextFields = () => {
    if (!showContextSection) return null;

    const effectiveProjectId =
      lockedProject?.id ?? contextValues.project_id ?? null;

    return (
      <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-4">
        <h3 className="text-sm font-medium text-gray-900">
          {t("quality.checklist.selector.context_heading")}
        </h3>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {resolvedContextFields.date ? (
            <div>
              <PMDatePicker
                name="date"
                label={t("Date")}
                value={contextValues.date || ""}
                onChange={(e) =>
                  contextFormik.setFieldValue("date", e?.target?.value || "")
                }
                placeholder={t("Date")}
              />
            </div>
          ) : null}

          {resolvedContextFields.customer && !lockedCustomer?.id ? (
            <div className="sm:col-span-1">
              <CustomerSelector
                selected={null}
                zIndex={NESTED_SELECTOR_Z_INDEX}
                {...contextFormik}
              />
            </div>
          ) : null}

          {resolvedContextFields.project && !lockedProject?.id ? (
            <div className="sm:col-span-1">
              <ProjectSelector
                selected={null}
                zIndex={NESTED_SELECTOR_Z_INDEX}
                {...contextFormik}
              />
            </div>
          ) : null}

          {resolvedContextFields.folder ? (
            <div className="sm:col-span-1">
              <ProjectFolderSelector
                projectId={effectiveProjectId}
                project={lockedProject}
                selected={lockedFolder}
                zIndex={NESTED_SELECTOR_Z_INDEX}
                {...contextFormik}
              />
            </div>
          ) : null}

          {resolvedContextFields.responsible ? (
            <div className="sm:col-span-1">
              <EmployeeSelector
                name="responsible_executive_id"
                label={t("Responsible Executive")}
                selected={selectedResponsible}
                zIndex={NESTED_SELECTOR_Z_INDEX}
                onChange={(e) => {
                  setSelectedResponsible(e?.target?.object ?? null);
                }}
                {...contextFormik}
              />
            </div>
          ) : null}
        </div>
      </div>
    );
  };

  const renderTypeStatusBadge = (type) => {
    const badge = getChecklistTypeHseBadge(type);

    if (badge.kind === "approved") {
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800">
          <ShieldCheckIcon className="h-3.5 w-3.5" />
          {badge.version
            ? t("quality.checklist.selector.status_hse_approved", { version: badge.version })
            : t("quality.checklist.selector.status_hse_linked")}
        </span>
      );
    }

    if (badge.kind === "pending") {
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">
          {badge.version
            ? t("quality.checklist.selector.status_hse_pending", { version: badge.version })
            : t("quality.checklist.selector.status_hse_pending_short")}
        </span>
      );
    }

    if (badge.kind === "draft") {
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
          {badge.version
            ? t("quality.checklist.selector.status_hse_draft", { version: badge.version })
            : t("quality.checklist.selector.status_hse_draft_short")}
        </span>
      );
    }

    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700">
        {t("quality.checklist.selector.status_unlinked")}
      </span>
    );
  };

  const renderItemPickerPanel = () => {
    if (!selectedType) {
      return (
        <div className="flex h-full min-h-[240px] flex-col items-center justify-center rounded-lg border border-dashed border-gray-200 bg-gray-50 px-6 py-10 text-center lg:min-h-[min(70vh,720px)]">
          <p className="text-sm font-medium text-gray-700">
            {t("quality.checklist.selector.choose_type_first")}
          </p>
          <p className="mt-1 text-xs text-gray-500">
            {t("quality.checklist.selector.choose_type_first_hint")}
          </p>
        </div>
      );
    }

    return (
      <div className="flex h-full min-h-[280px] flex-col rounded-lg border border-gray-200 bg-gray-50 lg:min-h-[min(70vh,720px)]">
        <div className="flex items-start justify-between gap-3 border-b border-gray-200 px-4 py-3">
          <div className="min-w-0">
            <h4 className="text-sm font-medium text-gray-900">
              {t("quality.checklist.selector.choose_items", {
                title: selectedType.title,
              })}
            </h4>
            {selectedItems.length > 0 && typeItems.length > 0 ? (
              <p className="mt-1 text-xs text-gray-600">
                {t("quality.checklist.selector.items_selected", {
                  selected: selectedItems.length,
                  total: typeItems.length,
                })}
              </p>
            ) : null}
          </div>
          {typeItems.length > 0 ? (
            <button
              type="button"
              onClick={toggleSelectAll}
              className="shrink-0 text-xs font-medium text-blue-600 hover:text-blue-800"
            >
              {selectedItems.length === typeItems.length
                ? t("quality.checklist.selector.deselect_all")
                : t("quality.checklist.selector.select_all")}
            </button>
          ) : null}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          {isLoadingItems ? (
            <ResourceLoading />
          ) : typeItems.length === 0 ? (
            <p className="text-sm text-gray-500">
              {t("quality.checklist.selector.no_items_in_type")}
            </p>
          ) : (
            <div className="space-y-2">
              {typeItems.map((item) => (
                <label
                  key={item.id}
                  className={`flex items-start rounded-lg border p-3 cursor-pointer transition-colors ${
                    selectedItems.includes(item.id)
                      ? "border-blue-500 bg-blue-50"
                      : "border-gray-200 bg-white hover:bg-gray-50"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedItems.includes(item.id)}
                    onChange={() => toggleItemSelection(item.id)}
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <div className="ml-3 min-w-0 flex-1">
                    <div className="text-sm font-medium text-gray-900">{item.title}</div>
                    {item.description ? (
                      <div className="mt-1 text-xs text-gray-500">{item.description}</div>
                    ) : null}
                  </div>
                  {selectedItems.includes(item.id) ? (
                    <CheckIcon className="ml-2 h-5 w-5 shrink-0 text-blue-600" />
                  ) : null}
                </label>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (isLoading) {
      return <ResourceLoading />;
    }

    if (error) {
      return <div role="alert"><ResourceError errors={[error]} /><button type="button" className="mt-3 text-sm font-semibold text-blue-700 underline" onClick={() => loadChecklistTypes(searchQuery)}>{t("common.retry")}</button></div>;
    }

    return (
      <div className="space-y-4">
        {renderContextFields()}

        {!typeOnly && <div className="space-y-3">
          <h3 className="text-lg font-medium text-gray-900">
            {t("quality.checklist.selector.mode_heading")}
          </h3>

          <div className="space-y-2">
            {showEmptyOption ? (
              <label className="flex items-center p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  name="checklist_mode"
                  value="new"
                  checked={mode === "new"}
                  onChange={handleNewChecklist}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                />
                <div className="ml-3 flex-1">
                  <div className="text-sm font-medium text-gray-900">
                    {t("quality.checklist.selector.mode_empty")}
                  </div>
                  <div className="text-xs text-gray-500">
                    {t("quality.checklist.selector.mode_empty_hint")}
                  </div>
                </div>
              </label>
            ) : null}

            {canCopy ? (
              <label className="flex items-center p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  name="checklist_mode"
                  value="main_report"
                  checked={mode === "main_report"}
                  onChange={handleCopySelect}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                />
                <div className="ml-3 flex-1">
                  <div className="text-sm font-medium text-gray-900">
                    {t("quality.checklist.selector.mode_copy")}
                  </div>
                  <div className="text-xs text-gray-500">
                    {t("quality.checklist.selector.mode_copy_hint", {
                      source: copySource.label || t("quality.checklist.selector.copy_source"),
                    })}
                  </div>
                </div>
              </label>
            ) : null}

            {showTypeOption ? (
              <label className="flex items-center p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  name="checklist_mode"
                  value="type"
                  checked={mode === "type"}
                  onChange={() => setMode("type")}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                />
                <div className="ml-3 flex-1">
                  <div className="text-sm font-medium text-gray-900">
                    {t("quality.checklist.selector.mode_type")}
                  </div>
                  <div className="text-xs text-gray-500">
                    {t("quality.checklist.selector.mode_type_hint")}
                  </div>
                </div>
              </label>
            ) : null}
          </div>
        </div>}

        {showTitleField ? (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("quality.checklist.selector.title_label")}
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => {
                setTitleTouched(true);
                setTitle(e.target.value);
              }}
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder={t("quality.checklist.selector.title_placeholder")}
            />
          </div>
        ) : null}

        {mode === "main_report" && copySource.checklist ? (
          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h4 className="text-sm font-medium text-gray-900 mb-2">
              {t("quality.checklist.selector.copy_from", {
                source: copySource.label || t("quality.checklist.selector.copy_source"),
              })}
            </h4>
            <div className="space-y-2">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="main_report_mode"
                  value="all"
                  checked={copyMode === "all"}
                  onChange={() => setCopyMode("all")}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                />
                <span className="ml-2 text-sm text-gray-700">
                  {t("quality.checklist.selector.copy_all", {
                    count: (copySource.checklist.items || []).length,
                  })}
                </span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="main_report_mode"
                  value="non_approved"
                  checked={copyMode === "non_approved"}
                  onChange={() => setCopyMode("non_approved")}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                />
                <span className="ml-2 text-sm text-gray-700">
                  {t("quality.checklist.selector.copy_non_approved", {
                    count: (copySource.checklist.items || []).filter((item) => !item.isApproved)
                      .length,
                  })}
                </span>
              </label>
            </div>
          </div>
        ) : null}

        {mode === "type" && showTypeOption ? (
          <div className={typeOnly ? "mt-4" : "mt-4 lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)] lg:items-stretch lg:gap-6"}>
            <div className="space-y-4 min-h-0">
              <p className="text-xs text-gray-600">
                {typeOnly ? t("reportWizard.defaultChecklist") : t("quality.checklist.selector.type_guidance")}
              </p>

              <div className="relative">
                <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  type="search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t("quality.checklist.selector.search_placeholder")}
                  className="block w-full rounded-md border border-gray-300 py-2 pl-9 pr-3 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  autoComplete="off"
                />
              </div>

              <div className="flex min-h-0 flex-col">
                <h4 className="mb-2 text-sm font-medium text-gray-900">
                  {t("quality.checklist.selector.choose_type")}
                </h4>
                {isSearchingTypes ? (
                  <div className="py-6">
                    <ResourceLoading />
                    <p className="mt-2 text-center text-xs text-gray-500">
                      {t("searching")}
                    </p>
                  </div>
                ) : !checklistTypes.length ? (
                  <ResourceEmpty
                    text={
                      searchQuery.trim()
                        ? t("quality.checklist.selector.no_search_results")
                        : t("quality.checklist.selector.no_types")
                    }
                  />
                ) : (
                  <div className="grid max-h-[40vh] grid-cols-1 gap-2 overflow-y-auto pr-1 lg:max-h-[min(70vh,720px)]">
                    {checklistTypes.map((type) => (
                      <button
                        key={type.id}
                        type="button"
                        aria-pressed={selectedType?.id === type.id}
                        onClick={() => handleTypeSelect(type)}
                        className={`w-full rounded-lg border p-3 text-left transition-colors ${
                          selectedType?.id === type.id
                            ? "border-blue-500 bg-blue-50"
                            : "border-gray-200 hover:border-blue-300 hover:bg-blue-50"
                        }`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <h5 className="text-sm font-medium text-gray-900">{type.title}</h5>
                              {renderTypeStatusBadge(type)}
                            </div>
                            {type.description ? (
                              <p className="mt-1 line-clamp-2 text-xs text-gray-500">
                                {type.description}
                              </p>
                            ) : null}
                          </div>
                          <span className="inline-flex shrink-0 items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">
                            {getChecklistTypeSelectorItems(type).length}{" "}
                            {t("quality.checklist.selector.points")}
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {!typeOnly && <div className="mt-4 min-h-0 lg:mt-0">{renderItemPickerPanel()}</div>}
          </div>
        ) : null}
      </div>
    );
  };

  const confirmDisabled =
    (mode === "type" && (isLoading || isSearchingTypes || Boolean(error) || !selectedType || (!typeOnly && selectedItems.length === 0))) ||
    (mode === "main_report" && !copySource.checklist);

  return (
    <Dialog
      as="div"
      className="fixed inset-0 overflow-y-auto"
      style={{ zIndex: 3000 }}
      open={open}
      onClose={onClose}
    >
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />

        <div className={`relative flex max-h-[92vh] w-full transform flex-col overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:max-w-2xl sm:p-6 ${typeOnly ? "" : "lg:max-w-6xl xl:max-w-7xl"}`}>
          <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
            <button
              type="button"
              className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              onClick={onClose}
            >
              <span className="sr-only">{t("Close")}</span>
              <XMarkIcon className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>

          <Dialog.Title className="mb-4 shrink-0 text-lg font-medium leading-6 text-gray-900">
            {t("quality.checklist.selector.title")}
          </Dialog.Title>

          <div className="min-h-0 flex-1 overflow-y-auto">{renderContent()}</div>

          <div className="mt-6 flex shrink-0 justify-end gap-3 border-t border-gray-100 pt-4">
            <button
              type="button"
              className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              onClick={onClose}
            >
              {t("Avbryt")}
            </button>
            <button
              type="button"
              className="inline-flex justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleConfirm}
              disabled={confirmDisabled}
            >
              {t("quality.checklist.selector.confirm")}
            </button>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
